import React, { useEffect, useState, useRef } from 'react';

import wordList from './resources/words.json';

const MAX_TYPED_KEYS = 30;
const WORD_ANIMATION_INTERVAL = 200;

const getWord = (wordList: string[]) => {
  const randomIndex = Math.floor(Math.random() * wordList.length);
  const word = wordList[randomIndex];
  return word.toLowerCase();
};

const isValidKey = (key: string, word: string) => {
  if (!word) return false;
  const result = word.split('').includes(key);
  return result;
};

type WProps = {
  word: string;
  validKeys: string[];
};

const Word = ({ word, validKeys }: WProps) => {
  if (!word) return null;

  const joinedKeys = validKeys.join('');
  const matched = word.slice(0, joinedKeys.length);
  const remainder = word.slice(joinedKeys.length);

  const matchedClass = joinedKeys === word ? 'matched completed' : 'matched';

  return (
    <>
      <span className={matchedClass}>{matched}</span>
      <span className='remainder'>{remainder}</span>
    </>
  );
};

type LCWProps = {
  completedWords: string[];
};

const ListCompletedWords = ({ completedWords }: LCWProps) => {
  if (!completedWords) return null;

  return (
    <>
      <ol>
        {completedWords.map((word) => (
          <li key={word}>{word}</li>
        ))}
      </ol>
    </>
  );
};

const App: React.FC = () => {
  const [typedKeys, setTypedKeys] = useState<string[]>([]);
  const [validKeys, setValidKeys] = useState<string[]>([]);
  const [word, setWord] = useState<string>('');
  const [completedWords, setCompletedWords] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setWord(getWord(wordList));
    if (containerRef.current) containerRef.current.focus();
  }, []);

  useEffect(() => {
    const wordFromValidKeys = validKeys.join('').toLowerCase();
    let timeout: NodeJS.Timeout;
    if (word && word === wordFromValidKeys) {
      timeout = setTimeout(() => {
        let newWord = '';

        do {
          newWord = getWord(wordList);
        } while (completedWords.includes(newWord));

        setWord(newWord);
        setValidKeys([]);
        setCompletedWords((prevCompletedWords) => [
          ...prevCompletedWords,
          word,
        ]);
      }, WORD_ANIMATION_INTERVAL);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [validKeys, word, completedWords]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const { key } = event;

    setTypedKeys((prevTypedKeys) =>
      [...prevTypedKeys, key].slice(MAX_TYPED_KEYS * -1)
    );

    if (isValidKey(key, word)) {
      setValidKeys((prevValidKeys) => {
        const isValidLength = prevValidKeys.length <= word.length;
        const isNextChar = isValidLength && word[prevValidKeys.length] === key;
        return isNextChar ? [...prevValidKeys, key] : prevValidKeys;
      });
    }
  };

  return (
    <div
      className='container'
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={containerRef}
    >
      <div className='valid-keys'>
        <Word word={word} validKeys={validKeys} />
      </div>
      <div className='typed-keys'>{typedKeys ? typedKeys.join(' ') : null}</div>
      <div className='completed-words'>
        <ListCompletedWords completedWords={completedWords} />
      </div>
    </div>
  );
};

export default App;
